import React, { useState, useEffect } from 'react';
import { useDbUser } from '../hooks/useDbUser';
import { db, sql, Task } from '../lib/db';
import RegularTaskList from '../components/tasks/RegularTaskList';
import { toast } from 'sonner';
import { calculateNextDueDate } from '../lib/dateUtils';
import { useTimer } from '../contexts/TimerContext';

function RegularTasksPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const { timerState } = useTimer();
  const [templates, setTemplates] = useState<Task[]>([]);
  const [todaysTasks, setTodaysTasks] = useState<Task[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<Task[]>([]);
  const [todaysProgress, setTodaysProgress] = useState<{ total: number; completed: number }>({ total: 0, completed: 0 });
  const [lastCompletions, setLastCompletions] = useState<Map<string, Date | null>>(new Map());
  const [loading, setLoading] = useState(true);
  const [migrationNeeded, setMigrationNeeded] = useState(false);

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadRegularTasks();
    } else if (!dbUserLoading && !dbUserId) {
      setLoading(false);
    }
  }, [dbUserId, dbUserLoading]);


  const loadRegularTasks = async () => {
    if (!dbUserId) return;
    
    try {
      // Load regular task templates, today's tasks, overdue tasks, and progress
      const [templateTasks, todayTasks, overdueTasks, progress] = await Promise.all([
        db.tasks.findRegularTaskTemplates(dbUserId),
        db.tasks.findTodaysRegularTasks(dbUserId),
        db.tasks.findOverdueRegularTasks(dbUserId),
        db.tasks.getTodaysProgress(dbUserId)
      ]);
      
      // Get unique task titles from all tasks
      const allTasks = [...templateTasks, ...todayTasks, ...overdueTasks];
      const uniqueTitles = [...new Set(allTasks.map(t => t.title))];
      
      // Fetch last completions for all tasks
      const completions = await db.regularTaskHistory.getLastCompletionsForTasks(uniqueTitles, dbUserId);
      
      setTemplates(templateTasks);
      setTodaysTasks(todayTasks);
      setOverdueTasks(overdueTasks);
      setTodaysProgress(progress);
      setLastCompletions(completions);
      
      // Check if we got an empty array due to missing migration
      if (templateTasks.length === 0 && todayTasks.length === 0 && overdueTasks.length === 0) {
        // Check if the migration is needed
        const checkResult = await sql`
          SELECT column_name 
          FROM information_schema.columns 
          WHERE table_name = 'tasks' 
          AND column_name IN ('is_daily', 'is_regular')
        `;
        if (checkResult.length === 0) {
          setMigrationNeeded(true);
        }
      }
    } catch (error) {
      console.error('Error loading regular tasks:', error);
      setTemplates([]);
      setTodaysTasks([]);
      setOverdueTasks([]);
    } finally {
      setLoading(false);
    }
  };


  const handleTaskCreate = async (task: Partial<Task>) => {
    if (!dbUserId) return;
    
    try {
      const frequency = task.frequency || 'Daily';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Set the start date if not provided (ensure UTC)
      const providedDate = task.start_date ? new Date(task.start_date) : today;
      const startDate = new Date(Date.UTC(
        providedDate.getUTCFullYear(),
        providedDate.getUTCMonth(),
        providedDate.getUTCDate(),
        0, 0, 0, 0
      ));
      
      // For regular tasks, next_due_date should be the same as start_date initially
      // This represents when the task is first due
      const nextDueDate = startDate;
      
      // Create the regular task
      const newTask = await db.tasks.create({
        ...task,
        user_id: dbUserId,
        title: task.title || '',
        priority: task.priority || 'Medium',
        position: templates.length,
        is_regular: true,
        frequency: frequency,
        recurrence_day: task.recurrence_day,
        start_date: startDate,
        next_due_date: nextDueDate.toISOString().split('T')[0],
        status: 'To Do',
        task_type: 'To Do'
      } as Omit<Task, 'id' | 'created_at' | 'updated_at'>);
      
      toast.success('Regular task created successfully');
      
      // Reload tasks to show the new task
      await loadRegularTasks();
    } catch (error) {
      console.error('Error creating regular task:', error);
      toast.error('Failed to create regular task');
    }
  };

  const handleTaskUpdate = async (id: string, updates: Partial<Task> & { _timerCompletion?: boolean }) => {
    if (!dbUserId) return;
    
    try {
      // Check if this is from timer completion
      const isTimerCompletion = updates._timerCompletion;
      // Remove the flag from updates
      const { _timerCompletion, ...taskUpdates } = updates;
      
      // First, fetch the current task to check if it's a regular task
      const currentTask = await db.tasks.findById(id, dbUserId);
      if (!currentTask) {
        console.error('Task not found');
        return;
      }
      
      // If this is a regular task being completed, handle it specially
      if (taskUpdates.status === 'Complete' && currentTask.is_regular) {
        // For regular tasks, we need to mark the current instance as complete
        // AND generate the next instance
        
        // First, mark the current task as complete
        const totalMinutes = await db.timeEntries.getTotalMinutesForTask(id);

        await db.tasks.update(id, dbUserId, {
          status: 'Complete',
          time_spent: totalMinutes > 0 ? totalMinutes : undefined,
          time_spent_minutes: totalMinutes > 0 ? totalMinutes : undefined
        });
        
        await db.regularTaskHistory.recordCompletion(id, dbUserId);
        await db.taskCompletionStatistics.recordCompletion({
          userId: dbUserId,
          taskId: id,
          taskTitle: currentTask.title,
          taskDescription: currentTask.description,
          priority: currentTask.priority,
          minutesSpent: totalMinutes
        });

        const updatedTask = {
          ...currentTask,
          status: 'Complete' as const,
          time_spent: totalMinutes,
          time_spent_minutes: totalMinutes
        };
        const frequency = updatedTask.frequency || 'Daily';
        
        // Use the current task's start_date (or next_due_date) as the base for calculating next occurrence
        // This ensures we generate the task for the next period, not the same day
        // IMPORTANT: Handle DATE columns properly - they come back as midnight local time
        let currentDueDate: Date;
        if (updatedTask.next_due_date) {
          // next_due_date is a DATE column from PostgreSQL
          // PostgreSQL DATE columns come back as YYYY-MM-DD strings
          // We need to parse this as UTC midnight, not local time
          const dateValue = updatedTask.next_due_date;
          if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // It's already in YYYY-MM-DD format from PostgreSQL
            currentDueDate = new Date(dateValue + 'T00:00:00.000Z');
          } else {
            // Fallback for other date formats
            const dateStr = new Date(dateValue).toISOString().split('T')[0];
            currentDueDate = new Date(dateStr + 'T00:00:00.000Z');
          }
        } else if (updatedTask.start_date) {
          // start_date is TIMESTAMPTZ, but normalize to UTC midnight for consistency
          const dateStr = new Date(updatedTask.start_date).toISOString().split('T')[0];
          currentDueDate = new Date(dateStr + 'T00:00:00.000Z');
        } else {
          // Fallback to today at UTC midnight
          const now = new Date();
          const dateStr = now.toISOString().split('T')[0];
          currentDueDate = new Date(dateStr + 'T00:00:00.000Z');
        }
        
        // EXTENSIVE DEBUG LOGGING - Task dates before calculation
        console.log('=== TASK UPDATE DEBUG START ===');
        console.log('1. Current task details:', {
          taskId: updatedTask.id,
          taskTitle: updatedTask.title,
          start_date: updatedTask.start_date ? new Date(updatedTask.start_date).toISOString() : 'null',
          next_due_date: updatedTask.next_due_date ? new Date(updatedTask.next_due_date).toISOString() : 'null',
          currentDueDate: currentDueDate.toISOString(),
          frequency: frequency,
          recurrence_day: updatedTask.recurrence_day,
          is_regular: updatedTask.is_regular,
          status: updatedTask.status
        });
        
        // Log current time info
        const now = new Date();
        const todayUTC = new Date(Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0, 0, 0, 0
        ));
        console.log('2. Current time info:', {
          now: now.toISOString(),
          nowUTC: now.toUTCString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          localTime: now.toLocaleString(),
          utcOffset: now.getTimezoneOffset(),
          todayUTC: todayUTC.toISOString(),
          todayUTCString: todayUTC.toISOString().split('T')[0]
        });
        
        // Calculate next due date from the current due date (not completion date)
        const nextDueDate = calculateNextDueDate(currentDueDate, frequency, updatedTask.recurrence_day);
        
        // Debug logging after calculation
        console.log('3. Next due date calculation:', {
          input_currentDueDate: currentDueDate.toISOString(),
          input_frequency: frequency,
          input_recurrenceDay: updatedTask.recurrence_day,
          output_nextDueDate: nextDueDate.toISOString(),
          nextDueDateUTC: nextDueDate.toUTCString(),
          nextDueDateLocal: nextDueDate.toLocaleString()
        });
        
        // Compare dates
        console.log('4. Date comparison:', {
          currentDueDate_Date: currentDueDate.toDateString(),
          nextDueDate_Date: nextDueDate.toDateString(),
          areSameDay: currentDueDate.toDateString() === nextDueDate.toDateString(),
          daysDifference: Math.floor((nextDueDate.getTime() - currentDueDate.getTime()) / (1000 * 60 * 60 * 24))
        });
        
        // Record the task generation if not already recorded
        const taskDate = updatedTask.start_date || new Date();
        const hasHistory = await db.regularTaskHistory.hasGeneratedForDate(updatedTask.id, dbUserId, taskDate);
        if (!hasHistory) {
          // Use the task's own ID as the template ID for history tracking
          await db.regularTaskHistory.recordGeneration(updatedTask.id, updatedTask.id, dbUserId, taskDate);
        }
        
        // Log SQL query parameters
        const nextDueDateString = nextDueDate.toISOString().split('T')[0];
        console.log('5. Duplicate check SQL parameters:', {
          userId: dbUserId,
          title: updatedTask.title,
          description: updatedTask.description || '',
          frequency: frequency,
          nextDueDate: nextDueDate.toISOString(),
          nextDueDateString: nextDueDateString,
          sqlDateCheck: `Looking for tasks with next_due_date = '${nextDueDateString}' OR DATE(start_date) = '${nextDueDateString}'`,
          todayString: new Date().toISOString().split('T')[0],
          isSameAsToday: nextDueDateString === new Date().toISOString().split('T')[0]
        });
        
        // Check if a task already exists for the next due date to avoid duplicates
        // Use timezone-safe date comparison
        // Check both start_date and next_due_date to match how findTodaysRegularTasks works
        const existingNextTask = await sql`
          SELECT id, title, start_date, next_due_date, frequency, status FROM tasks
          WHERE user_id = ${dbUserId}
          AND LOWER(TRIM(title)) = LOWER(TRIM(${updatedTask.title}))
          AND LOWER(TRIM(COALESCE(description, ''))) = LOWER(TRIM(COALESCE(${updatedTask.description || ''}, '')))
          AND frequency = ${frequency}
          AND is_regular = true
          AND status != 'Complete'
          AND (
            next_due_date = ${nextDueDateString}::date
            OR DATE(start_date AT TIME ZONE 'UTC') = ${nextDueDateString}
          )
          LIMIT 1
        `;
        
        console.log('6. Duplicate check result:', {
          foundExisting: existingNextTask.length > 0,
          existingTaskCount: existingNextTask.length,
          existingTask: existingNextTask.length > 0 ? {
            id: existingNextTask[0].id,
            title: existingNextTask[0].title,
            start_date: existingNextTask[0].start_date,
            next_due_date: existingNextTask[0].next_due_date,
            frequency: existingNextTask[0].frequency,
            status: existingNextTask[0].status
          } : 'none'
        });
        
        if (existingNextTask.length === 0) {
          // Additional safety check: ensure we're not creating a task for today
          // Use UTC date for consistent comparison
          const now = new Date();
          const todayUTC = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate(),
            0, 0, 0, 0
          ));
          const todayString = todayUTC.toISOString().split('T')[0];
          
          if (nextDueDateString === todayString && frequency === 'Daily') {
            console.log('7. WARNING: Attempted to create daily task for today, skipping');
            console.log('This should not happen - daily tasks should be created for tomorrow');
            console.log('Debug info:', {
              nextDueDateString,
              todayString,
              nextDueDate: nextDueDate.toISOString(),
              todayUTC: todayUTC.toISOString()
            });
            return;
          }
          
          console.log('7. Creating new task for next occurrence');
          
          // Check if next due date is today
          const nextDueDateMidnight = new Date(nextDueDate);
          nextDueDateMidnight.setHours(0, 0, 0, 0);
          const isToday = nextDueDateMidnight.getTime() === todayUTC.getTime();
          
          // Create the next instance only if it doesn't already exist
          const nextTask = await db.tasks.create({
            goal_id: updatedTask.goal_id,
            user_id: dbUserId,
            title: updatedTask.title,
            description: updatedTask.description,
            priority: updatedTask.priority,
            status: 'To Do',
            task_type: 'To Do',
            assigned_to: updatedTask.assigned_to,
            position: updatedTask.position,
            is_regular: true,
            frequency: frequency,
            recurrence_day: updatedTask.recurrence_day,
            start_date: nextDueDate,
            next_due_date: nextDueDate.toISOString().split('T')[0],
            sop: updatedTask.sop, // Copy SOP from the completed task
            // Only set is_live to true if the task is due today
            is_live: isToday
          });
          
          console.log('8. New task created:', {
            newTaskId: nextTask.id,
            newTaskTitle: nextTask.title,
            newTaskStartDate: nextTask.start_date ? new Date(nextTask.start_date).toISOString() : 'null',
            newTaskNextDueDate: nextTask.next_due_date ? new Date(nextTask.next_due_date).toISOString() : 'null',
            frequency: nextTask.frequency
          });
          
          // Record in history for the next task using the original task ID as template
          await db.regularTaskHistory.recordGeneration(updatedTask.id, nextTask.id, dbUserId, nextDueDate);
        } else {
          console.log('7. NOT creating new task - duplicate already exists');
        }
        
        console.log('=== TASK UPDATE DEBUG END ===');
        
        // Reload tasks after a delay to show the animation
        const delay = isTimerCompletion ? 500 : 100;
        setTimeout(async () => {
          await loadRegularTasks();
        }, delay);
        
        // Don't show toast for task completion (handled in RegularTaskList)
        return;
      }
      
      // For non-regular tasks or non-completion updates, proceed with normal update
      let updatedTask: Task;
      if (Object.keys(taskUpdates).length === 0) {
        // If updates is empty, use the current task
        updatedTask = currentTask;
      } else {
        updatedTask = await db.tasks.update(id, dbUserId, taskUpdates);
      }
      
      if (updatedTask.status === 'Complete') {
        const totalMinutes = await db.timeEntries.getTotalMinutesForTask(id);
        if (totalMinutes > 0) {
          await db.tasks.update(id, dbUserId, {
            time_spent: totalMinutes,
            time_spent_minutes: totalMinutes
          });
        }
        await db.taskCompletionStatistics.recordCompletion({
          userId: dbUserId,
          taskId: id,
          taskTitle: updatedTask.title,
          taskDescription: updatedTask.description,
          priority: updatedTask.priority,
          minutesSpent: totalMinutes
        });
      }

      // Update the local state based on the update type
      if (updatedTask.status === 'Complete' && !updatedTask.is_regular) {
        // Remove completed non-regular tasks from UI
        setTemplates(templates.filter(task => task.id !== id));
        setTodaysTasks(todaysTasks.filter(task => task.id !== id));
        setOverdueTasks(overdueTasks.filter(task => task.id !== id));
      } else if (!updatedTask.is_regular) {
        // Update non-regular task in place
        setTemplates(templates.map(task => task.id === id ? updatedTask : task));
        setTodaysTasks(todaysTasks.map(task => task.id === id ? updatedTask : task));
        setOverdueTasks(overdueTasks.map(task => task.id === id ? updatedTask : task));
      } else {
        // For regular task non-completion updates, reload to ensure consistency
        await loadRegularTasks();
      }
      
      // Don't show toast for task completion (handled in RegularTaskList)
      if (taskUpdates.status !== 'Complete') {
        toast.success('Task updated successfully');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleTaskDelete = async (id: string) => {
    if (!dbUserId) return;
    
    // Check if timer is running for this task or any regular task instance
    if (timerState.isRunning && timerState.task?.id === id) {
      toast.error('Cannot delete task while timer is running. Please stop the timer first.');
      return;
    }
    
    try {
      // Find the task to get its details
      const taskToDelete = templates.find(t => t.id === id) || 
                          todaysTasks.find(t => t.id === id) || 
                          overdueTasks.find(t => t.id === id);
      
      if (!taskToDelete) {
        toast.error('Task not found');
        return;
      }
      
      // Check if timer is running for any instance of this regular task
      if (taskToDelete.is_regular && timerState.isRunning && timerState.task) {
        // Check if the running task matches this regular task
        if (timerState.task.title === taskToDelete.title &&
            timerState.task.description === taskToDelete.description &&
            timerState.task.is_regular) {
          toast.error('Cannot delete this regular task while timer is running on one of its instances. Please stop the timer first.');
          return;
        }
      }
      
      // If it's a regular task, delete all instances with same title/description/frequency
      if (taskToDelete.is_regular) {
        const success = await db.tasks.deleteRegularTask(
          taskToDelete.title,
          taskToDelete.description || '',
          taskToDelete.frequency || 'Daily',
          dbUserId
        );
        
        if (success) {
          // Reload all tasks to reflect the deletion
          await loadRegularTasks();
          toast.success('Regular task deleted successfully');
        } else {
          toast.error('Failed to delete regular task');
        }
      } else {
        // For non-regular tasks, use normal delete
        const success = await db.tasks.delete(id, dbUserId);
        if (success) {
          // Remove from templates
          setTemplates(templates.filter(task => task.id !== id));
          // Remove from today's tasks if there
          setTodaysTasks(todaysTasks.filter(task => task.id !== id));
          // Remove from overdue tasks if there
          setOverdueTasks(overdueTasks.filter(task => task.id !== id));
          toast.success('Task deleted successfully');
        }
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  if (dbUserLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Regular Tasks</h1>
          <p className="mt-2 text-gray-600">
            These tasks regenerate automatically based on their frequency (daily, weekly, monthly, quarterly, or annually). Complete them to build consistent habits!
          </p>
        </div>

        {migrationNeeded && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-yellow-800 mb-2">Database Migration Required</h3>
            <p className="text-sm text-yellow-700 mb-3">
              The Regular Tasks feature requires a database migration to be applied. Please run the following migration:
            </p>
            <code className="block bg-yellow-100 p-2 rounded text-xs text-yellow-900 mb-3">
              psql $DATABASE_URL &lt; migrations/007_regular_tasks.sql
            </code>
            <p className="text-xs text-yellow-600">
              Contact your administrator if you need help applying this migration.
            </p>
          </div>
        )}

        {dbUserId && (
          <>
            <RegularTaskList
              templates={templates}
              todaysTasks={todaysTasks}
              overdueTasks={overdueTasks}
              todaysProgress={todaysProgress}
              lastCompletions={lastCompletions}
              dbUserId={dbUserId}
              onTaskCreate={handleTaskCreate}
              onTaskUpdate={handleTaskUpdate}
              onTaskDelete={handleTaskDelete}
            />
          </>
        )}
    </div>
  );
}

export default RegularTasksPage;
